import { resolvePostProcedureOutcomeClinicalState } from "../../cycles/presenters/postProcedureOutcomeState";

/**
 * The outcome fields this decision needs, named as the cycle API names them.
 *
 * `type` alone is not enough and neither is the acknowledged stage. Home reaches
 * the acknowledged-positive surface when the route says positive and the patient
 * acknowledged it, and *nothing after that re-reads the record*. The fields below
 * are the ones that change without `type` changing.
 */
export type EarlyPregnancyGuidanceOutcome = {
  type?: string | null;
  resultStatus?: string | null;
  /** `entered_in_error` — the result was retracted rather than corrected. */
  recordStatus?: string | null;
  verificationStatus?: string | null;
  /** A birth. Set on the same row; `type` stays `"pregnancy"`. */
  pregnancyOutcome?: string | null;
  /** A loss, in any of its recorded forms. `type` stays `"pregnancy"`. */
  miscarriageOutcome?: string | null;
};

/**
 * Whether Home should tell someone what the first weeks usually hold.
 *
 * Two things have to be true, and the acknowledged stage proves neither.
 *
 * **There has to still be a pregnancy.** A birth is recorded in
 * `pregnancyOutcome` and a loss in `miscarriageOutcome`, and *neither touches
 * `type`* — so an outcome still reads `"pregnancy"` after it has concluded either
 * way. Without this check a patient who had just miscarried was shown a first
 * scan "a few weeks after the result" and told to keep taking what the clinic
 * prescribed. `resolvePostProcedureOutcomeClinicalState` covers the rest of the
 * ways a positive stops being one — retracted as entered in error, refuted by the
 * clinic, cancelled, withdrawn, or never resulted — and using it here rather than
 * a second opinion keeps this surface agreeing with every other reader of the
 * same record.
 *
 * **It has to be the person who carried the cycle.** This content tells someone
 * to keep taking their prescribed medication, that their own symptoms are worth a
 * call, and which emergency number to use. None of that is addressed to a
 * supporting partner, and a partner acting on it is acting on instructions meant
 * for someone else. Supporter-voiced copy for this phase is a content decision;
 * until it exists the section is absent rather than misdirected.
 *
 * With no outcome record at all this returns false. There is then nothing that
 * says a pregnancy is being followed, and this content may only follow one.
 */
export function shouldShowEarlyPregnancyGuidance(input: {
  outcome: EarlyPregnancyGuidanceOutcome | null | undefined;
  viewerIsSupporting: boolean;
}): boolean {
  if (input.viewerIsSupporting) return false;
  if (!input.outcome) return false;

  if (resolvePostProcedureOutcomeClinicalState(input.outcome) !== "pregnancy") {
    return false;
  }

  // Concluded either way: a birth and a loss both end the stretch this is for.
  return !input.outcome.pregnancyOutcome && !input.outcome.miscarriageOutcome;
}
